import { useLayoutEffect, useRef, useState } from "react";
import { Dimensions, Modal, Pressable, StyleSheet, View } from "react-native";
import { Button, IconButton, Text } from "react-native-paper";
import SignatureCanvas from "react-signature-canvas";

import { colors } from "@/lib/theme";

const CARD_HEIGHT = Dimensions.get("window").height * 0.4;

interface Props {
    visible: boolean;
    onDone: (base64: string) => void;
    onClose: () => void;
    title?: string;
}

export function SignatureModal({ visible, onDone, onClose, title }: Props) {
    const signatureRef = useRef<SignatureCanvas>(null);
    const wrapperRef = useRef<View>(null);
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

    useLayoutEffect(() => {
        if (!visible) {
            return;
        }
        const node = wrapperRef.current as unknown as HTMLElement | null;
        if (!node || typeof node.getBoundingClientRect !== "function") {
            return;
        }
        const rect = node.getBoundingClientRect();
        setCanvasSize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
    }, [visible]);

    const handleClear = () => {
        signatureRef.current?.clear();
    };

    const handleConfirm = () => {
        const pad = signatureRef.current;
        if (!pad || pad.isEmpty()) {
            return;
        }
        const dataUrl = pad.getCanvas().toDataURL("image/png");
        onDone(dataUrl);
    };

    return (
        <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
            <Pressable style={styles.backdrop} onPress={onClose} />
            <View style={styles.card}>
                <View style={styles.header}>
                    <Text style={styles.title}>{title ?? "Sign here"}</Text>
                    <IconButton icon="close" size={20} onPress={onClose} />
                </View>
                <View ref={wrapperRef} style={styles.canvasWrapper}>
                    {canvasSize.width > 0 && canvasSize.height > 0 && (
                        <SignatureCanvas
                            ref={signatureRef}
                            penColor="#000"
                            backgroundColor="#fff"
                            canvasProps={{
                                width: canvasSize.width,
                                height: canvasSize.height,
                                style: { display: "block" }
                            }}
                        />
                    )}
                </View>
                <View style={styles.footer}>
                    <Button mode="outlined" onPress={handleClear}>
                        Clear
                    </Button>
                    <Button mode="contained" onPress={handleConfirm}>
                        Done
                    </Button>
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.4)"
    },
    card: {
        height: CARD_HEIGHT,
        backgroundColor: "#fff",
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        overflow: "hidden"
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.surfaceBorder
    },
    title: {
        fontSize: 16,
        fontWeight: "600",
        color: colors.textPrimary
    },
    canvasWrapper: {
        flex: 1,
        borderWidth: 1,
        borderColor: "#ddd",
        margin: 12,
        borderRadius: 8,
        overflow: "hidden",
        backgroundColor: "#fff"
    },
    footer: {
        flexDirection: "row",
        justifyContent: "space-between",
        paddingHorizontal: 16,
        paddingBottom: 24,
        paddingTop: 8
    }
});
